import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Field, NativeSelect, Toggle, Section } from "@/components/tax/shared";

const MARITAL = [
  { value: "single", label: "Single" }, { value: "married", label: "Married" }, { value: "common_law", label: "Common-law" },
  { value: "separated", label: "Separated" }, { value: "divorced", label: "Divorced" }, { value: "widowed", label: "Widowed" },
];

const SITUATIONS = [
  ["has_disability_certificate", "I have an approved Disability Tax Credit certificate (T2201)"],
  ["foreign_income", "I had income from outside Canada"],
  ["foreign_property_over_100k", "I held foreign property costing more than $100,000"],
  ["sold_principal_residence", "I sold my home (principal residence)"],
  ["crypto", "I bought, sold or earned cryptocurrency"],
  ["moved_for_work", "I moved 40 km+ closer to a new job or school"],
  ["first_time_filer", "This is my first Canadian tax return"],
];

function Dependants({ list, onChange, disabled }) {
  const set = (i, k, v) => onChange(list.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  return (
    <div className="space-y-2" data-testid="tax-dependants">
      {list.map((d, i) => (
        <div key={i} className="flex gap-2 items-end">
          <Field id={`tax-dep-year-${i}`} label="Birth year" type="number" value={d.birth_year || ""} disabled={disabled}
            onChange={(e) => set(i, "birth_year", e.target.value)} />
          <Field id={`tax-dep-rel-${i}`} label="Relationship" value={d.relationship || ""} disabled={disabled}
            onChange={(e) => set(i, "relationship", e.target.value)} />
          <Button variant="ghost" size="sm" disabled={disabled} data-testid={`tax-dep-remove-${i}`} onClick={() => onChange(list.filter((_, j) => j !== i))}>Remove</Button>
        </div>
      ))}
      <Button variant="outline" size="sm" disabled={disabled} data-testid="tax-dep-add" onClick={() => onChange([...list, { birth_year: "", relationship: "Child" }])}>+ Add dependant</Button>
    </div>
  );
}

export function ProfileStep({ ret, onSave, saving, locked }) {
  const [p, setP] = useState({});
  const [a, setA] = useState({});
  useEffect(() => {
    setP({ ...ret.personal, sin: "", spouse_sin: "" });
    setA({ ...ret.answers });
  }, [ret.id, ret.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const sp = (k) => (e) => setP({ ...p, [k]: e.target.value });
  const sa = (k) => (e) => setA({ ...a, [k]: e.target.value });
  const hasSpouse = ["married", "common_law"].includes(a.marital_status);
  const save = () => {
    const personal = { ...p };
    if (!personal.sin) delete personal.sin;
    if (!personal.spouse_sin) delete personal.spouse_sin;
    onSave({ personal, answers: a });
  };

  return (
    <div className="space-y-5" data-testid="tax-profile-step">
      <Section title="About you" subtitle="Your SIN is encrypted and only shown masked. Only the preparer you authorize can reveal it.">
        <div className="grid md:grid-cols-2 gap-4">
          <Field id="tax-legal-name" label="Legal name (as registered with CRA)" value={p.legal_name || ""} onChange={sp("legal_name")} disabled={locked} />
          <Field id="tax-sin" label={`Social Insurance Number ${ret.personal.sin_masked ? `(on file: ${ret.personal.sin_masked})` : ""}`}
            placeholder={ret.personal.sin_masked ? "Leave blank to keep" : "123 456 789"} value={p.sin || ""} onChange={sp("sin")} disabled={locked} autoComplete="off" />
          <Field id="tax-dob" label="Date of birth" type="date" value={p.dob || ""} onChange={sp("dob")} disabled={locked} />
          <Field id="tax-phone" label="Phone" value={p.phone || ""} onChange={sp("phone")} disabled={locked} />
          <Field id="tax-address" label="Street address" value={p.address || ""} onChange={sp("address")} disabled={locked} />
          <div className="grid grid-cols-2 gap-3">
            <Field id="tax-city" label="City" value={p.city || ""} onChange={sp("city")} disabled={locked} />
            <Field id="tax-postal" label="Postal code" value={p.postal_code || ""} onChange={sp("postal_code")} disabled={locked} />
          </div>
        </div>
      </Section>

      <fieldset disabled={locked} className="space-y-5 disabled:opacity-80">
      <Section title="Household" subtitle="Marital status and dependants on December 31.">
        <div className="grid md:grid-cols-2 gap-4">
          <NativeSelect id="tax-marital" label="Marital status" value={a.marital_status} options={MARITAL} onChange={(v) => setA({ ...a, marital_status: v })} />
          <NativeSelect id="tax-province" label="Province of residence on Dec 31" value={a.province_dec31 || "ON"}
            options={[{ value: "ON", label: "Ontario" }, { value: "OTHER", label: "Another province / territory" }]} onChange={(v) => setA({ ...a, province_dec31: v })} />
          {hasSpouse && <>
            <Field id="tax-spouse-name" label="Spouse / partner name" value={p.spouse_name || ""} onChange={sp("spouse_name")} disabled={locked} />
            <Field id="tax-spouse-income" label="Spouse's net income (line 23600)" type="number" value={a.spouse_net_income ?? ""} onChange={sa("spouse_net_income")} />
          </>}
        </div>
        <Toggle id="tax-resident-full-year" label="I lived in Canada for the whole year" checked={a.resident_full_year !== false} onChange={(v) => setA({ ...a, resident_full_year: v })} />
        {hasSpouse && <Toggle id="tax-claim-spouse" label="Claim the spouse or common-law partner amount" hint="Only if your partner's net income is low." checked={a.claim_spouse_amount} onChange={(v) => setA({ ...a, claim_spouse_amount: v })} />}
        <div className="mt-3"><Dependants list={a.dependants || []} onChange={(v) => setA({ ...a, dependants: v })} disabled={locked} /></div>
        {!hasSpouse && (a.dependants || []).length > 0 &&
          <Toggle id="tax-claim-eligible-dep" label="Claim the amount for an eligible dependant" checked={a.claim_eligible_dependant} onChange={(v) => setA({ ...a, claim_eligible_dependant: v })} />}
      </Section>

      <Section title="Deductions, credits & other income" subtitle="Leave blank anything that doesn't apply.">
        <div className="grid md:grid-cols-3 gap-4">
          <Field id="tax-rrsp-limit" label="RRSP deduction limit (from your NOA)" type="number" value={a.rrsp_deduction_limit ?? ""} onChange={sa("rrsp_deduction_limit")} />
          <Field id="tax-instalments" label="Tax instalments paid" type="number" value={a.instalments_paid ?? ""} onChange={sa("instalments_paid")} />
          <Field id="tax-self-employment" label="Net self-employment income" type="number" value={a.self_employment_net ?? ""} onChange={sa("self_employment_net")} />
          <Field id="tax-rental" label="Net rental income" type="number" value={a.rental_income_net ?? ""} onChange={sa("rental_income_net")} />
          <Field id="tax-capital-gains" label="Capital gains (not on slips)" type="number" value={a.capital_gains ?? ""} onChange={sa("capital_gains")} />
          <Field id="tax-other-income" label="Other income (not on slips)" type="number" value={a.other_income ?? ""} onChange={sa("other_income")} />
        </div>
        <div className="grid md:grid-cols-2 gap-x-6 mt-4">
          {SITUATIONS.map(([k, l]) => <Toggle key={k} id={`tax-q-${k}`} label={l} checked={a[k]} onChange={(v) => setA({ ...a, [k]: v })} />)}
        </div>
        <div className="mt-3">
          <label htmlFor="tax-notes" className="text-xs font-semibold text-slate-600">Anything else your preparer should know?</label>
          <textarea id="tax-notes" data-testid="tax-notes" rows={3} value={a.notes || ""} onChange={sa("notes")}
            className="w-full mt-1.5 rounded-md border border-input p-3 text-sm" />
        </div>
      </Section>
      </fieldset>

      <div className="flex justify-end">
        <Button data-testid="tax-profile-save-btn" disabled={saving || locked} onClick={save} className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8">
          {saving ? "Saving…" : "Save & continue"}
        </Button>
      </div>
    </div>
  );
}
